<template>
  <section class="py-space-md py-lg-space-xxl">
    <div class="container">
      <div class="row">
        <div class="text-center col-12 col-lg-8 offset-lg-2">
          <h2 class="h2-large mb-space-xs">
            {{ title }}
          </h2>
          <div v-html="description"></div>
          <div
            class="text-dark-1 fs-xs mb-space-md mb-lg-space-lg"
            v-html="footnote"
          ></div>
          <TabSwitcherApollo
            v-if="switchTabs"
            class="info-switch d-none d-lg-block mb-lg-space-sm"
            id="list-style-tab"
            :tabs="switchTabs"
            button-size="sm"
            active-tab-background="secondary"
            @tab-click="handleTabClick"
          >
          </TabSwitcherApollo>
        </div>
      </div>
      <!-- accordions desktop view -->
      <div class="row d-none d-lg-block">
        <div class="col-12 col-lg-10 offset-lg-1">
          <div class="px-lg-space-lg border slide-container">
            <div>
              <RdsAccordion :start-collapsed="true">
                <FinancialAidCollapse
                  v-for="(accordion, idx) in tabs[activeTabIdx]['accordions']"
                  :collapse-id="`${accordion.title.replaceAll(
                    ' ',
                    '-'
                  )}-${idx}`"
                  :key="idx"
                  :class="[
                    tabs[activeTabIdx]['accordions'].length - 1 === idx
                      ? ''
                      : 'border-bottom',
                  ]"
                  @collapse-shown="
                    handleCollapseEvent(
                      'open',
                      tabs[activeTabIdx]['title'],
                      accordion.title
                    )
                  "
                  @collapse-hidden="
                    handleCollapseEvent(
                      'close',
                      tabs[activeTabIdx]['title'],
                      accordion.title
                    )
                  "
                >
                  <template #title-block>
                    <div class="d-flex collapse-head">
                      <p class="title-text fs-xl mb-0">
                        {{ accordion.title }}
                      </p>
                      <p class="mb-0 text-dark-1">
                        {{ accordion.shortDescription }}
                      </p>
                    </div>
                  </template>
                  <template #default>
                    <div v-html="accordion.description"></div>
                  </template>
                </FinancialAidCollapse>
              </RdsAccordion>
            </div>
          </div>
        </div>
      </div>
      <!-- accordions mobile view -->
      <div class="row d-block d-lg-none">
        <div class="col-12 col-lg-10 offset-lg-1">
          <div v-for="(tab, i) in tabs" :key="`mobile-tab-${i}`">
            <h3 class="h3-large mb-space-xs">{{ tab.title }}</h3>
            <div
              class="px-space-sm border"
              :class="[tabs.length - 1 === i ? 'mb-0' : 'mb-space-lg']"
            >
              <RdsAccordion :start-collapsed="true">
                <FinancialAidCollapse
                  v-for="(accordion, idx) in tab.accordions"
                  :collapse-id="`mobile-${accordion.title.replaceAll(
                    ' ',
                    '-'
                  )}-${idx}`"
                  :key="`mobile-accordion-${idx}`"
                  :class="[
                    tab.accordions.length - 1 === idx ? '' : 'border-bottom',
                  ]"
                  @collapse-shown="
                    handleCollapseEvent('open', tab.title, accordion.title)
                  "
                  @collapse-hidden="
                    handleCollapseEvent('close', tab.title, accordion.title)
                  "
                >
                  <template #title-block>
                    <div>
                      <p class="fs-large mb-space-xxs">
                        {{ accordion.title }}
                      </p>
                      <p class="mb-0 text-dark-1">
                        {{ accordion.shortDescription }}
                      </p>
                    </div>
                  </template>
                  <template #default>
                    <div v-html="accordion.description"></div>
                  </template>
                </FinancialAidCollapse>
              </RdsAccordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import FinancialAidCollapse from "@/components/what-it-costs-page/FinancialAidCollapse.vue";
import { FinancialAid } from "@/server/resolvers/resolvers-types";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { RdsAccordion } from "@rds-vue-ui/rds-accordion";
import { TabSwitcherApollo } from "@rds-vue-ui/tab-switcher-apollo";

const props = defineProps<{
  title: string;
  description: string;
  footnote: string;
  tabs: FinancialAid["infoTabs"];
}>();

const activeTabIdx = ref<number>(0);

const handleTabClick = (e: Event, idx: number) => {
  e.preventDefault();
  activeTabIdx.value = idx;
  const sectionTitle = props.tabs[idx].title.toLowerCase();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    props.title.toLowerCase(),
    sectionTitle
  );
};

const handleCollapseEvent = (
  action: "open" | "close",
  section: string,
  text: string
) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action,
    "click",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
};

const switchTabs = computed(() => {
  return props.tabs.map((item, idx) => {
    return { tabId: idx.toString(), tabName: item.title };
  });
});
</script>

<style scoped lang="scss">
.info-switch :deep(.active-tab-button) {
  color: var(--rds-dark-3) !important;
}
.info-switch {
  & :deep(.tab-list) {
    padding: 4px !important;
    gap: 4px !important;
  }
  & :deep(.tab-list .highlight) {
    top: 4px !important;
    bottom: 4px !important;
  }
}
.slide-container {
  overflow-x: hidden;
}

@media screen and (min-width: 992px) {
  .collapse-head {
    align-items: center;
    gap: 32px;

    & .title-text {
      min-width: 300px;
    }
  }
}
</style>
